/**
 * Reset Password Page
 * ───────────────────
 * Step 2 of password reset: user arrives via email link with ?token=xxx
 * and sets a new password.
 *
 * States:
 *   1. Form — enter + confirm new password
 *   2. Success — password changed, link to sign in
 *   3. Error — invalid/expired token
 *
 * Place at: src/pages/ResetPasswordPage.jsx
 */

import React, { useState, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Sparkles, Lock, Eye, EyeOff, ArrowRight,
  CheckCircle, XCircle, ArrowLeft, Check
} from 'lucide-react';
import { authAPI } from '../api';
import { Button } from '../components/ui';

// ── Password strength rules ─────────────────────────────────────────────────

const PASSWORD_MIN = 8;

const usePasswordStrength = (password) => {
  return useMemo(() => {
    const checks = [
      { label: 'At least 8 characters', met: password.length >= PASSWORD_MIN },
      { label: 'Contains a number', met: /\d/.test(password) },
      { label: 'Contains uppercase', met: /[A-Z]/.test(password) },
      { label: 'Contains lowercase', met: /[a-z]/.test(password) },
    ];
    const score = checks.filter(c => c.met).length;
    return { checks, score, isValid: password.length >= PASSWORD_MIN };
  }, [password]);
};

// ── Component ───────────────────────────────────────────────────────────────

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const strength = usePasswordStrength(password);

  // No token in URL → show error immediately
  if (!token && !success) {
    return (
      <ResetShell>
        <div className="text-center py-4">
          <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center">
            <XCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">
            Invalid reset link
          </h1>
          <p className="text-zinc-400 mb-8">
            This link is missing or malformed. Please request a new password reset.
          </p>
          <Link
            to="/forgot-password"
            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium rounded-xl hover:from-purple-500 hover:to-indigo-500 transition-all"
          >
            Request new link
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </ResetShell>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!strength.isValid) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await authAPI.resetPassword(token, password);
      setSuccess(true);
    } catch (err) {
      const data = err?.response?.data;
      setError(
        data?.error || data?.msg || 'Something went wrong. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <ResetShell>
      {!success ? (
        <>
          {/* Back link */}
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-zinc-200 transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to sign in
          </Link>

          <h1 className="text-2xl font-bold text-white mb-2">
            Choose a new password
          </h1>
          <p className="text-zinc-500 mb-8">
            Enter your new password below. Make it strong.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* New password */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                New password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError('');
                  }}
                  placeholder="••••••••"
                  className="w-full bg-zinc-800/50 border border-zinc-700 rounded-xl pl-11 pr-12 py-3 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 transition-all"
                  autoFocus
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {/* Strength indicators */}
              {password.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {/* Strength bar */}
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                          strength.score >= level
                            ? strength.score <= 1
                              ? 'bg-red-500'
                              : strength.score <= 2
                              ? 'bg-amber-500'
                              : strength.score <= 3
                              ? 'bg-yellow-400'
                              : 'bg-emerald-400'
                            : 'bg-zinc-700'
                        }`}
                      />
                    ))}
                  </div>

                  {/* Check list */}
                  <div className="grid grid-cols-2 gap-1">
                    {strength.checks.map((check) => (
                      <div
                        key={check.label}
                        className={`flex items-center gap-1.5 text-xs transition-colors ${
                          check.met ? 'text-emerald-400' : 'text-zinc-500'
                        }`}
                      >
                        <Check className={`w-3 h-3 ${check.met ? 'opacity-100' : 'opacity-30'}`} />
                        {check.label}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Confirm password */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">
                Confirm password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (error) setError('');
                  }}
                  placeholder="••••••••"
                  className={`w-full bg-zinc-800/50 border rounded-xl pl-11 pr-12 py-3 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 transition-all ${
                    confirmPassword && confirmPassword !== password
                      ? 'border-red-500/50 focus:ring-red-500/30 focus:border-red-500/50'
                      : confirmPassword && confirmPassword === password
                      ? 'border-emerald-500/50 focus:ring-emerald-500/30 focus:border-emerald-500/50'
                      : 'border-zinc-700 focus:ring-purple-500/50 focus:border-purple-500/50'
                  }`}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {confirmPassword && confirmPassword !== password && (
                <p className="mt-1.5 text-xs text-red-400">Passwords don't match</p>
              )}
            </div>

            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                <p className="text-sm text-red-400">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              loading={loading}
              disabled={!strength.isValid || password !== confirmPassword}
              className="w-full py-3"
              icon={ArrowRight}
              iconPosition="right"
            >
              Reset Password
            </Button>
          </form>
        </>
      ) : (
        /* ── Success state ──────────────────────────────────────── */
        <div className="text-center py-4">
          <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-emerald-400" />
          </div>

          <h1 className="text-2xl font-bold text-white mb-3">
            Password updated
          </h1>
          <p className="text-zinc-400 mb-8">
            Your password has been reset successfully. You can now sign in with your new password.
          </p>

          <Button
            onClick={() => navigate('/login')}
            className="w-full py-3"
            icon={ArrowRight}
            iconPosition="right"
          >
            Sign in
          </Button>
        </div>
      )}
    </ResetShell>
  );
};

// ── Shell wrapper (shared layout for all states) ────────────────────────────

const ResetShell = ({ children }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      {/* Background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/3 -left-1/4 w-1/2 h-1/2 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/3 -right-1/4 w-1/2 h-1/2 bg-indigo-500/10 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2"
          >
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-white">Enumé</span>
          </button>
        </div>

        {/* Card */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8 backdrop-blur-sm">
          {children}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
